import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Content,
  ContentVariants,
  Divider,
  Flex,
  FlexItem,
  Label,
  LabelGroup
} from "@patternfly/react-core";

import {
  AuthContext,
  AppContext
} from "shared/context";

import redhatLogoWhite from "shared/assets/images/svg/linux/red-hat-logo-white-height-45px.svg";
import redhatLogoBlack from "shared/assets/images/svg/linux/red-hat-logo-height-45px.svg";
import rockyLogoWhite from "shared/assets/images/svg/linux/rocky-linux-white-height-45px.svg";
import rockyLogoBlack from "shared/assets/images/svg/linux/rocky-linux-height-45px.svg";
import debianLogoWhite from "shared/assets/images/svg/linux/debian-logo-white-height-45px.svg";
import debianLogoBlack from "shared/assets/images/svg/linux/debian-logo-height-45px.svg";

const EducationHeroCard = ({
  showImage = true
}) => {
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  const authContext = useContext(AuthContext);
  const appContext = useContext(AppContext);

  const reactAppFrontendUrl =
    appContext.state.react_app_frontend_url;

  const org =
    appContext.state.organizationSymbol;

  const logon =
    `${reactAppFrontendUrl}/${org}/ti/auth`;

  useEffect(() => {
    const updateTheme = () => {
      setIsDarkTheme(
        document.documentElement.classList.contains(
          "pf-v6-theme-dark"
        )
      );
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });

    return () => observer.disconnect();
  }, []);

  const RedHatLogo = isDarkTheme
    ? redhatLogoWhite
    : redhatLogoBlack;

  const RockyLogo = isDarkTheme
    ? rockyLogoWhite
    : rockyLogoBlack;

  const DebianLogo = isDarkTheme
    ? debianLogoWhite
    : debianLogoBlack;

  const handleAuth = () => {
    window.open(logon, "_self");
  };

  return (
  <Card isFullHeight variant="secondary" isGlass>
    <CardTitle>
      <Flex
        alignItems={{ default: "alignItemsCenter" }}
      >
        <FlexItem>
          <Content component={ContentVariants.h3}>
            Platforma edukacyjna
          </Content>
        </FlexItem>
        {!authContext.state.isAuthUser && (
          <FlexItem
            align={{
              default: "alignRight"
            }}
          >
            <Button
              onClick={handleAuth}
              variant="primary"
              size="sm"
            >
              Zaloguj się
            </Button>
          </FlexItem>
          )}
        </Flex>
    </CardTitle>

    <CardBody>
      <Content component={ContentVariants.p}>
        Platforma edukacyjna zapewnia dostęp do materiałów egzaminacyjnych, zbiorów zadań, analiz wyników egzaminów oraz nowoczesnych środowisk laboratoryjnych wykorzystywanych w branży IT.
        Uczniowie mogą rozwijać kompetencje w zakresie
        programowania, administracji systemami,
        konteneryzacji, wirtualizacji, automatyzacji
        i technologii chmurowych przy użyciu oVirt
        Virtualization, OpenShift Container Platform,
        Ceph Storage oraz Ansible Automation.
      </Content>

      <LabelGroup
        numLabels={4}
        collapsedText="${remaining} więcej"
        expandedText="Pokaż mniej"
        style={{ marginTop: "1rem" }}
      >
        <Label variant="outline">
          Arkusze egzaminacyjne
        </Label>

        <Label variant="outline">
          Zbiory zadań
        </Label>

        <Label variant="outline">
          Egzaminy próbne
        </Label>

        <Label variant="outline">
          Analizy egzaminów
        </Label>

        <Label variant="outline">
          oVirt Virtualization
        </Label>

        <Label variant="outline">
          OpenShift Container Platform
        </Label>

        <Label variant="outline">
          Ceph Storage
        </Label>

        <Label variant="outline">
          Ansible Automation
        </Label>
      </LabelGroup>
    </CardBody>

    <CardFooter>
      <Flex
        spaceItems={{
          default: "spaceItemsLg"
        }}
        alignItems={{
          default: "alignItemsCenter"
        }}
      >
        <FlexItem>
          <Button
            component="a"
            href="https://www.redhat.com"
            target="_blank"
            rel="noopener noreferrer"
            variant="plain"
          >
            <img
              src={RedHatLogo}
              alt="Red Hat"
              style={{
                height: "18px",
                position: "relative",
                top: "2px"
              }}
            />
          </Button>
        </FlexItem>

        <FlexItem>
          <Divider
            orientation={{ default: "vertical" }}
            style={{ height: "24px" }}
          />
        </FlexItem>

        <FlexItem>
          <Button
            component="a"
            href="https://rockylinux.org/pl-PL"
            target="_blank"
            rel="noopener noreferrer"
            variant="plain"
          >
            <img
              src={RockyLogo}
              alt="Rocky Linux"
              style={{
                height: "18px",
                position: "relative",
                top: "2px"
              }}
            />
          </Button>
        </FlexItem>

        <FlexItem>
          <Divider
            orientation={{ default: "vertical" }}
            style={{ height: "24px" }}
          />
        </FlexItem>

        <FlexItem>
          <Button
            component="a"
            href="https://www.debian.org/"
            target="_blank"
            rel="noopener noreferrer"
            variant="plain"
          >
            <img
              src={DebianLogo}
              alt="Debian"
              style={{
                height: "22px",
                position: "relative",
                top: "2px"
              }}
            />
          </Button>
        </FlexItem>
      </Flex>
    </CardFooter>

    {showImage && (
      <div
        role="img"
        aria-label="Platforma edukacyjna"
        style={{
          minHeight: "250px",
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundImage:
            "url(../../assets/images/pfbg_576.jpg)"
        }}
      />
    )}
  </Card>
);
};

export default EducationHeroCard;